<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import * as THREE from 'three'

const props = withDefaults(
  defineProps<{
    speed?: number
    centerWaveIntensity?: number
  }>(),
  {
    speed: 1.0,
    centerWaveIntensity: 0,
  },
)

const containerRef = ref<HTMLDivElement | null>(null)
let renderer: THREE.WebGLRenderer | null = null
let camera: THREE.PerspectiveCamera | null = null
let animationId: number | null = null

const init = () => {
  if (!containerRef.value) return

  const container = containerRef.value
  const width = container.clientWidth
  const height = container.clientHeight

  // 检测是否为移动端
  const isMobile = window.innerWidth < 768

  // Scene
  const scene = new THREE.Scene()
  scene.background = new THREE.Color('#ffffff')

  // Camera - 透视相机，从正前方看，更靠前
  camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000)
  camera.position.set(0, 6, 25)
  camera.lookAt(0, 0, -4)

  // Renderer
  renderer = new THREE.WebGLRenderer({ antialias: true })
  renderer.setSize(width, height)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  container.appendChild(renderer.domElement)

  // 根据设备类型调整网格密度
  const gridWidth = 70
  const gridDepth = 20
  const segmentsX = isMobile ? 250 : 300
  const segmentsZ = isMobile ? 250 : 300

  // 存储所有线条
  const horizontalLines: THREE.Line[] = []
  const verticalLines: THREE.Line[] = []

  // 预计算颜色
  const colorLeft = new THREE.Color('#4DD8E8')
  const colorMid = new THREE.Color('#A8C8E8')
  const colorRight = new THREE.Color('#C8B8E8')

  // 预计算x位置的基础颜色 - 两段独立的青色到紫色渐变，中间正弦波过渡
  const baseColors: THREE.Color[] = []
  for (let x = 0; x <= segmentsX; x++) {
    const xNorm = x / segmentsX
    const color = new THREE.Color()

    // 中间过渡区域 (0.42 - 0.58)，更宽的过渡
    const transitionStart = 0.42
    const transitionEnd = 0.58

    if (xNorm < transitionStart) {
      // 左半边：青色 → 紫色
      const t = xNorm / transitionStart // 0-1
      if (t < 0.5) {
        color.copy(colorLeft).lerp(colorMid, t * 2)
      } else {
        color.copy(colorMid).lerp(colorRight, (t - 0.5) * 2)
      }
    } else if (xNorm > transitionEnd) {
      // 右半边：青色 → 紫色，青色范围缩小
      const t = (xNorm - transitionEnd) / (1 - transitionEnd) // 0-1
      if (t < 0.3) {
        // 青色范围缩小到30%
        color.copy(colorLeft).lerp(colorMid, t / 0.3)
      } else {
        color.copy(colorMid).lerp(colorRight, (t - 0.3) / 0.7)
      }
    } else {
      // 中间过渡区：紫色 → 青色，使用正弦波平滑过渡
      const t = (xNorm - transitionStart) / (transitionEnd - transitionStart)
      const smoothT = (1 - Math.cos(t * Math.PI)) / 2 // 正弦波平滑
      color.copy(colorRight).lerp(colorLeft, smoothT)
    }
    baseColors.push(color)
  }

  // 创建横线
  for (let z = 0; z <= segmentsZ; z++) {
    const points: THREE.Vector3[] = []
    const colors: number[] = []

    for (let x = 0; x <= segmentsX; x++) {
      const px = (x / segmentsX - 0.5) * gridWidth
      const pz = (z / segmentsZ - 0.5) * gridDepth
      points.push(new THREE.Vector3(px, 0, pz))
      const c = baseColors[x]
      colors.push(c.r, c.g, c.b)
    }

    const geometry = new THREE.BufferGeometry().setFromPoints(points)
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))

    const material = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
    })

    const line = new THREE.Line(geometry, material)
    line.position.y = -6
    scene.add(line)
    horizontalLines.push(line)
  }

  // 创建纵线 - 间隔改小
  for (let x = 0; x <= segmentsX; x += 2) {
    const points: THREE.Vector3[] = []
    const colors: number[] = []
    const c = baseColors[x]

    for (let z = 0; z <= segmentsZ; z++) {
      const px = (x / segmentsX - 0.5) * gridWidth
      const pz = (z / segmentsZ - 0.5) * gridDepth
      points.push(new THREE.Vector3(px, 0, pz))
      colors.push(c.r, c.g, c.b)
    }

    const geometry = new THREE.BufferGeometry().setFromPoints(points)
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))

    const material = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
    })

    const line = new THREE.Line(geometry, material)
    line.position.y = -6
    scene.add(line)
    verticalLines.push(line)
  }

  let time = 0
  let centerWaveTime = 0

  // 山峰配置 - 山峰更靠后，离底部更远
  // 移动端山峰位置更集中，宽度稍窄
  const peaks = isMobile
    ? [
        // 移动端：山峰位置更集中在中间区域
        {
          x: -0.15,
          z: 0.25,
          height: 7,
          width: 0.06,
          speed: 2.7,
          phase: 0,
        },
        {
          x: -0.05,
          z: 0.25,
          height: 5,
          width: 0.05,
          speed: 3.6,
          phase: 0.8,
        },
        {
          x: 0.05,
          z: 0.25,
          height: 8,
          width: 0.07,
          speed: 2.25,
          phase: 2.0,
        },
        {
          x: 0.15,
          z: 0.25,
          height: 6,
          width: 0.055,
          speed: 3.15,
          phase: 1.2,
        },
        {
          x: 0.25,
          z: 0.25,
          height: 7,
          width: 0.06,
          speed: 2.0,
          phase: 2.8,
        },
      ]
    : [
        // 桌面端：保持原有配置
        {
          x: -0.42,
          z: 0.25,
          height: 8,
          width: 0.07,
          speed: 2.7,
          phase: 0,
        },
        {
          x: -0.28,
          z: 0.25,
          height: 5,
          width: 0.05,
          speed: 3.6,
          phase: 0.8,
        },
        {
          x: -0.12,
          z: 0.25,
          height: 9,
          width: 0.08,
          speed: 2.25,
          phase: 2.0,
        },
        {
          x: 0.05,
          z: 0.25,
          height: 6,
          width: 0.06,
          speed: 3.15,
          phase: 1.2,
        },
        {
          x: 0.22,
          z: 0.25,
          height: 8,
          width: 0.07,
          speed: 2.0,
          phase: 2.8,
        },
        {
          x: 0.38,
          z: 0.25,
          height: 7,
          width: 0.06,
          speed: 2.9,
          phase: 0.5,
        },
        {
          x: 0.48,
          z: 0.25,
          height: 5,
          width: 0.05,
          speed: 3.4,
          phase: 4.0,
        },
      ]

  // 中心波浪宽度
  const centerWidth = isMobile ? 0.06 : 0.08

  // 预计算高度的缓存
  const heightCache: Float32Array = new Float32Array(
    (segmentsX + 1) * (segmentsZ + 1),
  )
  const maxHeightCache: Float32Array = new Float32Array(
    (segmentsX + 1) * (segmentsZ + 1),
  )

  const getHeightFast = (xIdx: number, zIdx: number, t: number) => {
    const xNorm = xIdx / segmentsX
    const zNorm = zIdx / segmentsZ
    let y = 0

    for (let i = 0; i < peaks.length; i++) {
      const peak = peaks[i]
      const dx = xNorm - 0.5 - peak.x
      const dz = zNorm - 0.5 - peak.z
      const dist2 = dx * dx + dz * dz
      // 最低高度为峰值的40%，最高为100%
      const dynamicHeight =
        peak.height *
        (0.4 + 0.6 * (0.5 + 0.5 * Math.sin(t * peak.speed + peak.phase)))
      y += dynamicHeight * Math.exp(-dist2 / (2 * peak.width * peak.width))
    }

    // 中心波浪 - 在x=0.5位置，z=0.25位置（和其他山峰一样），形成一个山峰
    if (props.centerWaveIntensity > 0) {
      const dx = xNorm - 0.5 // 距离中心x轴的距离
      const dz = zNorm - 0.5 - 0.25 // 距离山峰z位置的距离，和其他山峰一致

      // 山峰高度 - 和y轴最高山峰一样高
      const maxHeight = 12 // 和最高山峰一致
      const waveHeight =
        maxHeight * (0.4 + 0.6 * (0.7 + 0.3 * Math.sin(centerWaveTime * 2.5)))

      // 使用高斯分布形成山峰形状
      const dist2 = dx * dx + dz * dz
      const peakFactor = Math.exp(-dist2 / (2 * centerWidth * centerWidth))

      y += waveHeight * peakFactor * props.centerWaveIntensity
    }
    // 底部区域逐渐衰减到平坦，避免露出白色背景
    // 将衰减起始点推后到0.8，让底部网格保持平直
    const bottomFadeStart = 0.85
    if (zNorm > bottomFadeStart) {
      const fadeProgress = (zNorm - bottomFadeStart) / (1 - bottomFadeStart)
      y *= 1 - fadeProgress
    }

    // 只允许向上起伏，不允许向下
    return Math.max(0, y)
  }

  const animate = () => {
    animationId = requestAnimationFrame(animate)
    time += 0.02 * props.speed
    centerWaveTime += 0.04

    // 预计算所有高度
    for (let x = 0; x <= segmentsX; x++) {
      for (let z = 0; z <= segmentsZ; z++) {
        const idx = x * (segmentsZ + 1) + z
        heightCache[idx] = getHeightFast(x, z, time)
      }
    }

    // 计算从前往后的最大高度（只用于遮挡山峰后面的线条）
    // maxHeightCache[idx] 存储的是从 z+1 到 segmentsZ 之间的最大高度
    for (let x = 0; x <= segmentsX; x++) {
      let runningMax = -Infinity
      for (let z = segmentsZ; z >= 0; z--) {
        const idx = x * (segmentsZ + 1) + z
        // 先存储当前位置前面的最大高度（不包括当前位置）
        maxHeightCache[idx] = runningMax
        // 然后更新 runningMax
        const h = heightCache[idx]
        runningMax = Math.max(runningMax, h)
      }
    }

    // 更新横线
    for (let z = 0; z <= segmentsZ; z++) {
      const line = horizontalLines[z]
      const positions = line.geometry.attributes.position.array as Float32Array
      const colors = line.geometry.attributes.color.array as Float32Array

      for (let x = 0; x <= segmentsX; x++) {
        const idx = x * (segmentsZ + 1) + z
        const y = heightCache[idx]
        positions[x * 3 + 1] = y

        // 检查遮挡 - 只有当前点低于前面的最大高度时才遮挡
        const maxYInFront = maxHeightCache[x * (segmentsZ + 1) + z]
        const isOccluded = y < maxYInFront

        const i3 = x * 3
        if (isOccluded) {
          colors[i3] = 1
          colors[i3 + 1] = 1
          colors[i3 + 2] = 1
        } else {
          const c = baseColors[x]
          colors[i3] = c.r
          colors[i3 + 1] = c.g
          colors[i3 + 2] = c.b
        }
      }
      line.geometry.attributes.position.needsUpdate = true
      line.geometry.attributes.color.needsUpdate = true
    }

    // 更新纵线
    for (let i = 0; i < verticalLines.length; i++) {
      const x = i * 2
      const line = verticalLines[i]
      const positions = line.geometry.attributes.position.array as Float32Array
      const colors = line.geometry.attributes.color.array as Float32Array

      for (let z = 0; z <= segmentsZ; z++) {
        const idx = x * (segmentsZ + 1) + z
        const y = heightCache[idx]
        positions[z * 3 + 1] = y

        // 检查遮挡 - 只有当前点低于前面的最大高度时才遮挡
        const maxYInFront = maxHeightCache[x * (segmentsZ + 1) + z]
        const isOccluded = y < maxYInFront

        const i3 = z * 3
        if (isOccluded) {
          colors[i3] = 1
          colors[i3 + 1] = 1
          colors[i3 + 2] = 1
        } else {
          const c = baseColors[x]
          colors[i3] = c.r
          colors[i3 + 1] = c.g
          colors[i3 + 2] = c.b
        }
      }
      line.geometry.attributes.position.needsUpdate = true
      line.geometry.attributes.color.needsUpdate = true
    }

    renderer!.render(scene, camera!)
  }

  animate()

  // Resize handler
  const onResize = () => {
    if (!containerRef.value || !renderer || !camera) return
    const w = containerRef.value.clientWidth
    const h = containerRef.value.clientHeight
    camera.aspect = w / h
    camera.updateProjectionMatrix()
    renderer.setSize(w, h)
  }

  window.addEventListener('resize', onResize)
}

onMounted(() => {
  init()
})

onBeforeUnmount(() => {
  if (animationId) cancelAnimationFrame(animationId)
  if (renderer && containerRef.value) {
    containerRef.value.removeChild(renderer.domElement)
    renderer.dispose()
  }
})
</script>

<template>
  <div ref="containerRef" class="wave-grid-container"></div>
</template>

<style scoped>
.wave-grid-container {
  width: 100%;
  height: 500px;
  position: relative;
  background: white;
  top: 285px;
}

@media (min-width: 768px) {
  .wave-grid-container {
    height: 750px;
    top: 50px;
  }
}

:deep(canvas) {
  display: block;
  outline: none;
}
</style>
